import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loader } from "@/components/shared/loader";
import { getNotificationEventLabel } from "@/features/notifications/constants/event-labels";
import {
  useMarkNotificationRead,
  useNotificationDetail,
} from "@/features/notifications/hooks/useNotifications";
import { formatOptionalArabicDateTime } from "@/lib/formatters";
import type { UserNotification } from "@/types/notification";

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-right text-sm font-medium">{value}</span>
    </div>
  );
}

interface NotificationDetailSheetProps {
  notificationId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview?: UserNotification;
}

export function NotificationDetailSheet({
  notificationId,
  open,
  onOpenChange,
  preview,
}: NotificationDetailSheetProps) {
  const { data, isLoading, error } = useNotificationDetail(notificationId, open);
  const markRead = useMarkNotificationRead();
  const markedRef = useRef<string | null>(null);
  const notification = data ?? preview;
  const actionUrl =
    typeof notification?.metadata?.url === "string"
      ? notification.metadata.url
      : undefined;

  useEffect(() => {
    if (!open || !notification || notification.readAt || !notification.id) return;
    if (markedRef.current === notification.id) return;
    markedRef.current = notification.id;
    void markRead.mutateAsync(notification.id);
  }, [open, notification, markRead]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-full overflow-y-auto sm:max-w-lg">
        <SheetHeader className="text-right">
          <SheetTitle>تفاصيل الإشعار</SheetTitle>
          <SheetDescription className="text-right">
            {notification
              ? getNotificationEventLabel(notification.templateKey)
              : "—"}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          {isLoading && (
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          )}
          {error && (
            <p className="text-right text-sm text-destructive">
              تعذر تحميل تفاصيل الإشعار.
            </p>
          )}
          {notification && !isLoading && (
            <>
              <div className="rounded-lg border border-border p-3">
                <Field label="النوع" value={getNotificationEventLabel(notification.templateKey)} />
                <Field label="العنوان" value={notification.title} />
                <Field
                  label="الحالة"
                  value={notification.readAt ? "مقروء" : "غير مقروء"}
                />
                <Field
                  label="التاريخ"
                  value={formatOptionalArabicDateTime(notification.createdAt)}
                />
                {notification.readAt && (
                  <Field
                    label="تاريخ القراءة"
                    value={formatOptionalArabicDateTime(notification.readAt)}
                  />
                )}
              </div>

              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">المحتوى</p>
                <p className="whitespace-pre-wrap rounded-md bg-muted p-3 text-sm leading-relaxed">
                  {notification.body}
                </p>
              </div>

              {actionUrl && (
                actionUrl.startsWith("http") ? (
                  <Button asChild className="w-full">
                    <a href={actionUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="ml-2 size-4" />
                      فتح المحتوى
                    </a>
                  </Button>
                ) : (
                  <Button asChild className="w-full">
                    <Link to={actionUrl} onClick={() => onOpenChange(false)}>
                      <ExternalLink className="ml-2 size-4" />
                      فتح المحتوى
                    </Link>
                  </Button>
                )
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
